import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Drawer, HStack, Text, VStack } from '@chakra-ui/react';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { SessionRoomEnded } from '@src/components/sco/Advisors/sections/SessionRoomEnded';
import { StreamSessionRoom } from '@src/components/sco/Advisors/sessions/StreamSessionRoom';
import { engagementChatHref } from '@src/components/sco/Advisors/utils/mapEngagements';
import { cleanErrorMessage, formatWhen, packageWindowEnded, personName } from '@src/components/sco/Advisors/utils/sessionFormat';
import { isJoinableSession, isNotJoinableError, isTerminalSession, sessionStatusPalette } from '@src/components/sco/Advisors/utils/sessionStatus';
import { ChatThread } from '@src/components/sco/Chat/ChatThread';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { COMPLETE_ADVISOR_SESSION } from '@src/gql/mutations/CompleteAdvisorSession';
import { CREATE_ADVISOR_SESSION_JOIN_CREDENTIALS } from '@src/gql/mutations/CreateAdvisorSessionJoinCredentials';
import { REPORT_ADVISOR_SESSION_PRESENCE } from '@src/gql/mutations/ReportAdvisorSessionPresence';
import { ADVISOR_SESSION, ADVISOR_SESSION_ROOM } from '@src/gql/queries/AdvisorSession';
import { ScoAdvisorSession } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LuCheck, LuMessageSquare, LuPhoneOff, LuVideo } from 'react-icons/lu';
type StreamCredentials = {
  apiKey?: string | null;
  token?: string | null;
  userId?: string | null;
  userName?: string | null;
  callType?: string | null;
  callId?: string | null;
  enabled?: boolean | null;
};
type JoinCredentialsData = {
  createAdvisorSessionJoinCredentials?: {
    credentials?: StreamCredentials | null;
  } | null;
};
type SessionQueryData = {
  advisorSession?: ScoAdvisorSession | null;
};
const SessionChatPane = t0 => {
  const $ = _c(13);
  const {
    threadId,
    reason,
    scheduledAt
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Text fontSize="sm" fontWeight="semibold">Session chat</Text>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== reason) {
    t2 = reason && <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="xs" lineClamp={2}>{reason}</Text>;
    $[1] = reason;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== scheduledAt) {
    t3 = scheduledAt && <Text color="colorPalette.fg/50" fontSize="xs">{formatWhen(scheduledAt)}</Text>;
    $[3] = scheduledAt;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Box borderBottomWidth="1px" flexShrink={0} paddingX={3} paddingY={3}>{t1}{t2}{t3}</Box>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== threadId) {
    t5 = <Box flex={1} minHeight={0}>{threadId ? <ChatThread bypassContentPermissions={true} hideHeader={true} enableFileAttachments={false} threadId={threadId} /> : <Text color="colorPalette.fg/50" fontSize="sm" padding={3}>Chat opens when this session is confirmed.</Text>}</Box>;
    $[8] = threadId;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <VStack align="stretch" gap={0} height="full" minHeight={0}>{t4}{t5}</VStack>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
export const SessionRoomSection = () => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const advisorSessionId = String(router.query.advisorSessionId || '').trim();
  const [credentials, setCredentials] = useState<StreamCredentials | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [packageOvertime, setPackageOvertime] = useState(false);
  const roomQuery = useQuery<SessionQueryData>(ADVISOR_SESSION_ROOM, {
    variables: {
      advisorSessionId
    },
    skip: !advisorSessionId,
    errorPolicy: 'all',
    pollInterval: 4000
  });
  const sessionQuery = useQuery<SessionQueryData>(ADVISOR_SESSION, {
    variables: {
      advisorSessionId
    },
    skip: !advisorSessionId || !roomQuery.error
  });
  const session = roomQuery.data?.advisorSession || sessionQuery.data?.advisorSession;
  const role = session?.currentUserRole;
  const counterpart = role === 'EXPERT' ? session?.seekerUser : session?.expertUser;
  const threadId = session?.chatThreadId || session?.chatThread?.threadId || null;
  const boardEngagement = session?.boardEngagement;
  const boardHref = boardEngagement ? engagementChatHref(boardEngagement, paths.base) : null;
  const statusKey = session?.statusKey;
  const terminal = !!session && isTerminalSession(statusKey);
  const joinable = isJoinableSession(statusKey);
  const detailHref = advisorSessionId ? `${paths.sessions}/${advisorSessionId}` : paths.sessions;
  useEffect(() => {
    const tick = () => setPackageOvertime(packageWindowEnded(session?.scheduledAt, session?.approximateLengthMinutes));
    tick();
    const id = window.setInterval(tick, 15000);
    return () => window.clearInterval(id);
  }, [session?.scheduledAt, session?.approximateLengthMinutes]);
  const [createCredentials, credState] = useMutation<JoinCredentialsData>(CREATE_ADVISOR_SESSION_JOIN_CREDENTIALS);
  const [completeSession, completeState] = useMutation(COMPLETE_ADVISOR_SESSION, {
    refetchQueries: [{
      query: ADVISOR_SESSION_ROOM,
      variables: {
        advisorSessionId
      }
    }]
  });
  const [reportPresenceMutation] = useMutation(REPORT_ADVISOR_SESSION_PRESENCE, {
    refetchQueries: [{
      query: ADVISOR_SESSION_ROOM,
      variables: {
        advisorSessionId
      }
    }]
  });
  const presentRef = useRef(false);
  const sessionIdRef = useRef(advisorSessionId);
  sessionIdRef.current = advisorSessionId;
  const reportPresenceRef = useRef(reportPresenceMutation);
  reportPresenceRef.current = reportPresenceMutation;
  const setPresent = useCallback(async (present: boolean) => {
    const id_0 = sessionIdRef.current;
    if (!id_0) {
      return;
    }
    if (present) {
      presentRef.current = true;
    } else if (!presentRef.current) {
      return;
    }
    try {
      await reportPresenceRef.current({
        variables: {
          input: {
            advisorSessionId: id_0,
            present
          }
        }
      });
    } catch {
      // Presence is best-effort. Server still fail-closes money transitions.
    }
    if (!present) {
      presentRef.current = false;
    }
  }, []);
  useEffect(() => {
    return () => {
      if (presentRef.current) {
        void setPresent(false);
      }
    };
  }, [setPresent]);
  const credentialsRequestedRef = useRef(false);
  const loadCredentials = useCallback(async () => {
    if (!advisorSessionId || !joinable) {
      return;
    }
    setError(null);
    try {
      const result = await createCredentials({
        variables: {
          input: {
            advisorSessionId
          }
        }
      });
      const creds = result.data?.createAdvisorSessionJoinCredentials?.credentials;
      setCredentials(creds || null);
      if (!creds) {
        setError('No credentials returned. Stream may be disabled on the server.');
      }
    } catch (err) {
      setCredentials(null);
      if (isNotJoinableError(err)) {
        return;
      }
      setError(cleanErrorMessage(err, 'Could not create join credentials.'));
    }
  }, [advisorSessionId, createCredentials, joinable]);
  useEffect(() => {
    credentialsRequestedRef.current = false;
  }, [advisorSessionId]);
  useEffect(() => {
    if (router.isReady && advisorSessionId && joinable && !credentials && !credentialsRequestedRef.current) {
      credentialsRequestedRef.current = true;
      void loadCredentials();
    }
  }, [router.isReady, advisorSessionId, joinable, credentials, loadCredentials]);
  useEffect(() => {
    if (terminal) {
      setCredentials(null);
    }
  }, [terminal]);
  useEffect(() => {
    if (credentials && credentials.enabled === false && joinable) {
      void setPresent(true);
    }
  }, [credentials, joinable, setPresent]);
  const handleComplete = async () => {
    setError(null);
    try {
      await completeSession({
        variables: {
          input: {
            advisorSessionId
          }
        }
      });
      setCompleted(true);
      presentRef.current = false;
    } catch (err_0) {
      setError(cleanErrorMessage(err_0, 'Could not complete session.'));
    }
  };
  const handleExit = () => {
    void setPresent(false);
    void router.push(detailHref);
  };
  const streamEnabled = credentials?.enabled !== false && !!credentials?.apiKey && !!credentials?.token;
  const showRecap = terminal || completed;
  return <VStack align={'stretch'} gap={0} height={'full'} minHeight={0} width={'full'}>
      <HStack borderBottomWidth={'1px'} flexShrink={0} gap={3} justify={'space-between'} paddingX={4} paddingY={2} wrap={'wrap'}>
        <HStack gap={3} minWidth={0}>
          <Button size={'sm'} variant={'ghost'} onClick={handleExit}>
            Exit
          </Button>
          <LuVideo />
          <Text fontWeight={'semibold'} lineClamp={1}>
            {personName(counterpart, 'Session')}
          </Text>
          {session?.status?.title && <Badge colorPalette={sessionStatusPalette(session.statusKey)} variant={'subtle'}>
              {session.status.title}
            </Badge>}
        </HStack>
        <HStack gap={2}>
          <Text color={'colorPalette.fg/55'} display={{
          base: 'none',
          md: 'block'
        }} fontSize={'xs'}>
            This session is recorded (audio). Transcripts are kept for 90 days.
          </Text>
          <Button display={{
          base: 'inline-flex',
          lg: 'none'
        }} size={'sm'} variant={'outline'} onClick={() => setChatOpen(true)}>
            <LuMessageSquare /> Chat
          </Button>
          {role === 'EXPERT' && !showRecap && <Button loading={completeState.loading} size={'sm'} variant={'outline'} onClick={() => void handleComplete()}>
              <LuPhoneOff /> End & complete
            </Button>}
        </HStack>
      </HStack>

      {error && <Text color={'red.fg'} fontFamily={'longform'} fontSize={'sm'} paddingX={4} paddingY={2}>
          {error}
        </Text>}
      {packageOvertime && joinable && !terminal && <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'} paddingX={4} paddingY={2}>
          Booked time is up — you can keep talking, no extra charge.
        </Text>}

      <HStack align={'stretch'} flex={1} gap={0} minHeight={0}>
        <Box bg={'bg.stage'} flex={1} minHeight={0} minWidth={0} position={'relative'}>
          {showRecap ? <SessionRoomEnded detailHref={detailHref} statusKey={statusKey} statusTitle={session?.status?.title} /> : <>
              {credState.loading && !credentials && <Text color={'fg.onStageMuted'} fontSize={'sm'} padding={6}>
                  Requesting join credentials…
                </Text>}
              {joinable && streamEnabled && credentials?.apiKey && credentials.token && credentials.userId && credentials.callType && credentials.callId && <StreamSessionRoom credentials={{
            apiKey: credentials.apiKey,
            token: credentials.token,
            userId: credentials.userId,
            userName: credentials.userName,
            callType: credentials.callType,
            callId: credentials.callId
          }} onLeft={handleExit} onJoined={() => {
            void setPresent(true);
          }} />}
              {joinable && !streamEnabled && credentials && <VStack align={'flex-start'} gap={3} padding={6}>
                  <Text color={'fg.onStage'} fontWeight={'semibold'}>
                    Video is not live
                  </Text>
                  <Text color={'fg.onStageMuted'} fontFamily={'longform'} fontSize={'sm'}>
                    Stream is disabled or missing keys on the API. Chat still works. You can complete the
                    session for ledger testing.
                  </Text>
                  {statusKey === 'schedule_confirmed' && <Text color={'fg.onStageMuted'} fontFamily={'longform'} fontSize={'sm'}>
                      Waiting for both people to join. The session starts automatically when the other party
                      is here.
                    </Text>}
                  <Button loading={credState.loading} size={'sm'} variant={'outline'} onClick={() => void loadCredentials()}>
                    Retry join
                  </Button>
                </VStack>}
            </>}
        </Box>

        <Box borderLeftWidth={'1px'} display={{
        base: 'none',
        lg: 'block'
      }} flexShrink={0} width={'360px'}>
          <SessionChatPane boardHref={boardHref} reason={session?.reason} scheduledAt={session?.scheduledAt} threadId={threadId} />
        </Box>
      </HStack>

      <Drawer.Root open={chatOpen} placement={'end'} onOpenChange={e => {
      if (!e.open) {
        setChatOpen(false);
      }
    }}>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content maxWidth={'100%'} width={'100%'}>
            <Drawer.CloseTrigger />
            <Box height={'100dvh'}>
              <SessionChatPane boardHref={boardHref} reason={session?.reason} scheduledAt={session?.scheduledAt} threadId={threadId} />
            </Box>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>

      {completed && !terminal && <HStack borderTopWidth={'1px'} padding={3}>
          <LuCheck />
          <Button asChild size={'sm'} variant={'cta'}>
            <NextLink href={detailHref}>Back to session detail</NextLink>
          </Button>
        </HStack>}
    </VStack>;
};